// src/pages/billing/index.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import CancelSubscriptionModal from "@/components/billing/CancelSubscriptionModal";
import CancelledBanner from "@/components/billing/CancelledBanner";

function moneyDisplay(m) {
  if (!m) return null;
  if (typeof m === "string") return m;
  return m.display || null;
}

export default function BillingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { businesses, currentBusinessId, roles, roleDetails, refreshMe } = useAuth();
  const location = useLocation();

  const biz = useMemo(() => {
    const id = currentBusinessId ? String(currentBusinessId) : null;
    return (businesses || []).find((b) => String(b.id) === id) || null;
  }, [businesses, currentBusinessId]);

  const isSuperAdmin = roles?.includes("Super Administrator");
  const isOwnerLike = useMemo(() => {
    if (isSuperAdmin) return true;
    const bid = currentBusinessId ? Number(currentBusinessId) : null;
    if (!bid) return false;
    const br = roleDetails?.business_roles || [];
    return br.some((x) => x.business_id === bid && x.role === "owner");
  }, [isSuperAdmin, roleDetails, currentBusinessId]);

  const CYCLE_LABELS = {
    monthly: t("billing.cycleMonthly"),
    yearly: t("billing.cycleYearly"),
    lifetime: t("billing.cycleLifetime"),
  };

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [currency, setCurrency] = useState("RON");
  const [currencies, setCurrencies] = useState(["RON"]);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [paying, setPaying] = useState(false);
  const [catalogBiz, setCatalogBiz] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);

// Dacă te întorci din Stripe cu ?success=1&session_id=cs_..., sincronizează imediat (nu aștepta webhook-ul).
useEffect(() => {
  const qs = new URLSearchParams(location.search || "");
  const success = qs.get("success");
  const sessionId = qs.get("session_id");

  if (success === "1" && sessionId) {
    (async () => {
      try {
        await api.post("/billing/sync-checkout", { session_id: sessionId });
      } catch (e) {
        // ignore: webhook-ul tot poate rezolva, iar UI se va reîmprospăta
      } finally {
        try {
          await refreshMe({ silent: true });
        } catch (e) {}
        // curăță querystring (evită re-trigger la refresh)
        qs.delete("success");
        qs.delete("session_id");
        const q = qs.toString();
        navigate(`${location.pathname}${q ? `?${q}` : ""}`, { replace: true });
      }
    })();
  }
}, [location.search, location.pathname, navigate, refreshMe]);


  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const { data } = await api.get("/billing/catalog");
        if (!alive) return;

        const pl = Array.isArray(data?.plans) ? data.plans : Array.isArray(data) ? data : [];
        const ad = Array.isArray(data?.addons) ? data.addons : [];
        setPlans(pl);
        setAddons(ad);
        if (data?.business) setCatalogBiz(data.business);

        const curList = Array.isArray(data?.currencies) && data.currencies.length
          ? data.currencies
          : [data?.currency || biz?.currency || "RON"];
        setCurrencies(curList);
        const cur0 = (curList[0] || "RON").toUpperCase();
        setCurrency(cur0);

        const pick =
          pl.find((p) => p.highlighted) ||
          pl.find((p) => (p.code || "").toLowerCase() === "pro") ||
          pl[0] ||
          null;

        setSelectedPlan(pick);

        // setează default cycle pe primul disponibil (pentru moneda curentă)
        const available = pick?.prices || {};
        const firstCycle = ["monthly", "yearly", "lifetime"].find((k) => available?.[k]?.[cur0]?.amount);
        if (firstCycle) setCycle(firstCycle);
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || "Nu am putut încărca planurile.";
        setErr(detail);
      } finally {
        alive && setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Dacă se schimbă moneda / planul, asigură-te că cycle e valid
  const availableCycles = useMemo(() => {
    const prices = selectedPlan?.prices || {};
    const keys = Object.keys(prices);
    const order = ["monthly", "yearly", "lifetime"];
    const sorted = keys.sort((a, b) => order.indexOf(a) - order.indexOf(b));
    return sorted
      .filter((k) => prices?.[k]?.[currency]?.amount)
      .map((k) => ({ key: k, label: CYCLE_LABELS[k] || k }));
  }, [selectedPlan, currency]);

  useEffect(() => {
    if (!availableCycles.length) return;
    if (!availableCycles.some((c) => c.key === cycle)) {
      setCycle(availableCycles[0].key);
    }
  }, [availableCycles, cycle]);

  // Dacă se schimbă cycle/currency, scoate add-on-urile care nu mai au preț
  useEffect(() => {
    setSelectedAddons((prev) =>
      (prev || []).filter((code) => {
        const a = addons.find((x) => x.code === code);
        return !!a?.prices?.[cycle]?.[currency]?.amount;
      })
    );
  }, [cycle, currency, addons]);

  const planMoney = selectedPlan?.prices?.[cycle]?.[currency];
  const addonsTotal = useMemo(() => {
    return (selectedAddons || []).reduce((sum, code) => {
      const a = addons.find((x) => x.code === code);
      const m = a?.prices?.[cycle]?.[currency];
      return sum + (m?.amount || 0);
    }, 0);
  }, [selectedAddons, addons, cycle, currency]);

  const totalDisplay = useMemo(() => {
    const total = (planMoney?.amount || 0) + addonsTotal;
    const value = (total / 100).toFixed(2).replace(/\.00$/, "");
    if (currency === "RON") return `${value} lei`;
    if (currency === "EUR") return `${value} €`;
    return `${value} ${currency}`;
  }, [addonsTotal, planMoney?.amount, currency]);

  const hasActiveSub =
    catalogBiz &&
    ["active", "trialing"].includes(catalogBiz?.subscription_status);

  const cancelAtPeriodEnd = !!catalogBiz?.cancel_at_period_end;
  const isLifetime = catalogBiz?.billing_cycle === "lifetime";
  const hasStripeSub = !!catalogBiz?.stripe_subscription_id && !isLifetime;

  const reloadCatalog = async () => {
    try {
      const { data } = await api.get("/billing/catalog");
      if (data?.business) setCatalogBiz(data.business);
      await refreshMe({ silent: true });
    } catch (_) {}
  };

  const onCancelConfirm = async ({ reason, comment }) => {
    setCancelling(true);
    try {
      await api.post("/billing/cancel", { reason, comment });
      setShowCancelModal(false);
      await reloadCatalog();
    } catch (e) {
      const detail =
        e?.response?.data?.detail ||
        e?.response?.data?.stripe?.[0] ||
        e?.response?.data?.subscription?.[0] ||
        e?.message ||
        "Nu am putut anula abonamentul.";
      setErr(detail);
    } finally {
      setCancelling(false);
    }
  };

  const onCheckout = async () => {
    if (!selectedPlan) return;
    if (selectedPlan?.is_enterprise) {
      setErr("Pentru Enterprise, cere ofertă (nu se poate plăti direct online). ");
      return;
    }
    if (!planMoney?.amount) {
      setErr("Plan indisponibil pentru combinația selectată (perioadă/monedă). Alege alt plan sau schimbă perioada/moneda.");
      return;
    }

    const missingAddon = (selectedAddons || []).find((code) => {
      const a = addons.find((x) => x.code === code);
      return !a?.prices?.[cycle]?.[currency]?.amount;
    });
    if (missingAddon) {
      setErr(`Add-on indisponibil pentru combinația selectată: ${missingAddon}`);
      return;
    }
    setPaying(true);
    setErr("");
    try {
      const payload = {
        plan_code: selectedPlan.code,
        billing_cycle: cycle,
        currency,
        addons: selectedAddons,
        trial: false,
      };
      const { data } = await api.post("/billing/checkout", payload);
      const url = data?.checkout_url || data?.url;
      if (!url) throw new Error("Nu am primit link-ul de plată.");
      window.location.href = url;
    } catch (e) {
      const detail =
        e?.response?.data?.detail ||
        e?.response?.data?.plan_code?.[0] ||
        e?.response?.data?.stripe?.[0] ||
        e?.message ||
        "Nu am putut porni plata.";
      setErr(detail);
    } finally {
      setPaying(false);
    }
  };

  if (!isOwnerLike) {
    return (
      <div className="min-h-screen bg-[#070B14] text-white flex items-center justify-center p-6">
        <div className="w-full max-w-xl rounded-2xl border border-white/10 bg-white/5 p-6">
          <h1 className="text-2xl font-semibold">Facturare</h1>
          <p className="text-white/70 mt-2">
            Doar Owner-ul business-ului poate gestiona abonamentul.
          </p>
          <button
            onClick={() => navigate("/app/subscription-suspended", { replace: true })}
            className="mt-6 px-4 py-3 rounded-xl border border-white/15 hover:bg-white/10"
          >
            Înapoi
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#070B14] text-white p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">{t("billing.choosePlan")}</h1>
            <p className="text-white/70 mt-1">
              Business: <span className="text-white">{biz?.name || "—"}</span>
            </p>
          </div>
          <button
            onClick={() => navigate("/app/subscription-suspended", { replace: true })}
            className="text-sm px-3 py-2 rounded-xl border border-white/15 hover:bg-white/10"
          >
            {t("common.back")}
          </button>
        </div>

        {loading ? (
          <div className="mt-6 text-white/70">{t("billing.loadingPlans")}</div>
        ) : null}

        {err ? (
          <div className="mt-6 rounded-xl border border-red-400/20 bg-red-400/10 p-3 text-sm text-red-100">
            {err}
          </div>
        ) : null}

        {!loading && hasActiveSub ? (
          <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="text-sm text-white/60 mb-3">{t("billing.currentSubscription")}</div>
            <div className="rounded-xl border border-white/10 bg-black/20 p-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-white/50">{t("billing.planLabel")}</div>
                  <div className="font-medium text-white">
                    {catalogBiz?.plan || catalogBiz?.plan_code || "—"}
                  </div>
                </div>
                <div>
                  <div className="text-white/50">{t("billing.period")}</div>
                  <div className="font-medium text-white">
                    {CYCLE_LABELS[catalogBiz?.billing_cycle] || catalogBiz?.billing_cycle || "—"}
                  </div>
                </div>
                <div>
                  <div className="text-white/50">{t("subscription.expiredAt")}</div>
                  <div className="font-medium text-white">
                    {catalogBiz?.current_period_end
                      ? new Date(catalogBiz.current_period_end).toLocaleDateString("ro-RO", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })
                      : "—"}
                  </div>
                </div>
              </div>

              {cancelAtPeriodEnd ? (
                <div className="mt-4">
                  <CancelledBanner
                    periodEnd={catalogBiz?.current_period_end}
                    onUndone={reloadCatalog}
                  />
                </div>
              ) : (
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="mt-4 text-sm px-4 py-2 rounded-xl border border-red-400/20 text-red-300 hover:bg-red-400/10"
                >
                  {t("billing.cancelSubscription")}
                </button>
              )}
            </div>
          </div>
        ) : null}

        {!loading && plans.length ? (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="text-sm text-white/60 mb-2">{t("billing.plans")}</div>
              <div className="space-y-2">
                {plans.map((p) => {
                  const active = selectedPlan?.id === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => {
                        setSelectedPlan(p);
                        const prices = p?.prices || {};
                        const first = ["monthly", "yearly", "lifetime"].find(
                          (k) => prices?.[k]?.[currency]?.amount
                        );
                        if (first) setCycle(first);
                      }}
                      className={[
                        "w-full text-left p-3 rounded-xl border",
                        active ? "border-white/40 bg-white/10" : "border-white/10 hover:bg-white/10",
                      ].join(" ")}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-medium">{p.name}</div>
                        {p.badge ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-white/10 border border-white/10">
                            {p.badge}
                          </span>
                        ) : null}
                      </div>
                      {p.description ? (
                        <div className="text-sm text-white/60 mt-1">{p.description}</div>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="text-sm text-white/60 mb-2">{t("billing.paymentSection")}</div>

              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <div className="text-lg font-semibold">{selectedPlan?.name || "—"}</div>
                <div className="text-white/60 text-sm mt-1">
                  {t("billing.choosePeriod")}
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {availableCycles.map((c) => (
                    <button
                      key={c.key}
                      onClick={() => setCycle(c.key)}
                      className={[
                        "px-3 py-2 rounded-xl border text-sm",
                        cycle === c.key
                          ? "border-white/40 bg-white/10"
                          : "border-white/10 hover:bg-white/10",
                      ].join(" ")}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>

                {currencies.length > 1 ? (
                  <div className="mt-4">
                    <div className="text-sm text-white/60">{t("billing.currency")}</div>
                    <select
                      value={currency}
                      onChange={(e) => setCurrency((e.target.value || "RON").toUpperCase())}
                      className="mt-2 w-full px-3 py-2 rounded-xl bg-black/30 border border-white/10"
                    >
                      {currencies.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}

                {addons.length ? (
                  <div className="mt-4">
                    <div className="text-sm text-white/60">{t("billing.addonsLabel")}</div>
                    <div className="mt-2 space-y-2">
                      {addons.map((a) => {
                        const m = a?.prices?.[cycle]?.[currency];
                        const disabled = !m?.amount;
                        const checked = selectedAddons.includes(a.code);
                        return (
                          <label
                            key={a.code}
                            className={[
                              "flex items-center justify-between gap-3 rounded-xl border px-3 py-2",
                              disabled ? "border-white/5 opacity-60" : "border-white/10 hover:bg-white/5",
                            ].join(" ")}
                          >
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                disabled={disabled}
                                checked={checked}
                                onChange={(e) => {
                                  const on = e.target.checked;
                                  setSelectedAddons((prev) => {
                                    const s = new Set(prev || []);
                                    if (on) s.add(a.code);
                                    else s.delete(a.code);
                                    return Array.from(s);
                                  });
                                }}
                              />
                              <span className="text-sm">{a.name}</span>
                            </div>
                            <span className="text-sm text-white/80 whitespace-nowrap">
                              {moneyDisplay(m) || "—"}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                <div className="mt-4 text-sm text-white/60">
                  {t("billing.priceLabel")}{" "}
                  <span className="text-white font-medium">
                    {moneyDisplay(planMoney) || "—"}
                  </span>
                </div>

                <div className="mt-2 text-sm text-white/60">
                  {t("billing.totalLabel")}:{" "}
                  <span className="text-white font-medium">{totalDisplay}</span>
                </div>

                <button
                  onClick={onCheckout}
                  disabled={!selectedPlan || !cycle || paying || !planMoney?.amount || !!selectedPlan?.is_enterprise}
                  className="mt-5 w-full px-4 py-3 rounded-xl bg-white text-black font-medium disabled:opacity-50"
                >
                  {paying ? t("billing.openingPayment") : t("billing.continueToPayment")}
                </button>

                <div className="mt-3 text-xs text-white/50">
                  {t("billing.stripeRedirect")}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <CancelSubscriptionModal
          open={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          onConfirm={onCancelConfirm}
          periodEnd={catalogBiz?.current_period_end}
          immediate={!hasStripeSub}
          loading={cancelling}
        />
      </div>
    </div>
  );
}
